// @flow

import axios from 'axios';
import omit from 'lodash';
import { TFunction } from 'react-i18next';
import authService from './auth-service';
import config from '../config';
import i18n from '../i18n';
import { isNotUndefined } from '../utils/utils';

const CUSTOM_ERR_CODES = [
  // if any of these codes are present in an error response, return the whole error object
  // as it will have custom handling
  'referral_already_imported',
  'US_MATCHING_HCN_DOB',
  'US_MATCHING_HCN',
  'US_MATCHING_DOB',
  'US_TIMEOUT'
];

type FallbackErrorMessageFunction = (t: TFunction) => string;

export type AxiosError = {
  response: {
    data: {
      statusCode: number,
      message: string,
      error?: {
        message: string,
      },
      errors?: any[],
    },
  },
  message: string,
};

type LocaleType = 'en-CA' | 'fr-CA';
const DEFAULT_LOCALE = 'en-CA';

export class ApiService {
  endpoint: string = config.API_ENDPOINT;
  locale: LocaleType = DEFAULT_LOCALE;

  get token(): ?string {
    return '';
  }

  get headers(): any {
    return {
      'Content-Type': 'application/json',
      'Accept-Language': i18n.language
    };
  }

  setLocale(locale: LocaleType): void {
    this.locale = locale;
  }

  getErrorMessageTranslation(
    error: AxiosError,
    fallbackErrorMessage?: FallbackErrorMessageFunction
  ) {
    const checkErrorCodeExistence = (code: string) =>
      i18n.exists(`errors:codes.${code}`);
    const errorsTFunction = i18n.getFixedT(i18n.language, 'errors');

    const { code } = error.response.data.error;
    const translationKey = `errors:codes.${code}`;

    const doesErrorCodeTranslationExist = checkErrorCodeExistence(code);

    if (error.message_code) {
      const messageCodeTranslationKey = `passwordFeedback:all.${error.message_code}`;
      const doesMessageCodeTransaltionExist = i18n.exists(
        messageCodeTranslationKey
      );

      if (doesMessageCodeTransaltionExist) {
        const translatedMessage = i18n.t(messageCodeTranslationKey);
        return translatedMessage;
      }
    }

    if (doesErrorCodeTranslationExist) {
      return errorsTFunction(translationKey);
    }

    if (isNotUndefined(fallbackErrorMessage)) {
      return fallbackErrorMessage(errorsTFunction);
    }

    return errorsTFunction('generic');
  }

  handleError(error: AxiosError, fallbackErrorMessage?: FallbackErrorMessageFunction) {
    const errorsTFunction = i18n.getFixedT(i18n.language, 'errors');

    if (error.response && error.response.data) {
      if (error.response.data.error && error.response.data.error.code) {
        if (CUSTOM_ERR_CODES.includes(error.response.data.error.code)) {
          return Promise.reject(error.response.data.error);
        }

        const errorMessage = this.getErrorMessageTranslation(
          error,
          fallbackErrorMessage
        );

        return Promise.reject(errorMessage);
      }

      if (error.response.data.errors) {
        const { errors } = error.response.data;

        const translatedErrors = errors.map((err: any) => {
          const translatedMessage = this.getErrorMessageTranslation(
            err,
            fallbackErrorMessage
          );
          return { ...err, translatedMessage: translatedMessage };
        });

        return Promise.reject(translatedErrors);
      }

      return Promise.reject(errorsTFunction('generic'));
    }

    if (isNotUndefined(fallbackErrorMessage)) {
      return Promise.reject(fallbackErrorMessage(errorsTFunction));
    }

    if (error.response && error.response.status >= 500) {
      return Promise.reject(errorsTFunction('generic'));
    }

    return Promise.reject(errorsTFunction('generic'));
  }

  get(
    url: string,
    options: any = {},
    fallbackErrorMessage?: FallbackErrorMessageFunction
  ): Promise<any> {
    const opts = Object.assign(
      {},
      {
        method: 'get',
        url: `${this.endpoint}${url}`,
        headers: this.headers
      },
      options
    );
    return axios(opts)
      .then((response) => response.data)
      .catch((err) => this.handleError(err, fallbackErrorMessage));
  }

  post(
    url: string,
    body: any,
    options: any = {},
    fallbackErrorMessage?: FallbackErrorMessageFunction
  ): Promise<any> {
    const opts = Object.assign(
      {},
      {
        url: `${this.endpoint}${url}`,
        method: 'post',
        data: JSON.stringify(body),
        headers: {
          ...this.headers,
          ...options.headers
        }
      },
      omit(options, ['headers'])
    );
    return axios(opts)
      .then((response) => response.data)
      .catch((err) => this.handleError(err, fallbackErrorMessage));
  }

  put(
    url: string,
    body: any,
    fallbackErrorMessage?: FallbackErrorMessageFunction
  ): Promise<any> {
    return axios({
      url: `${this.endpoint}${url}`,
      method: 'put',
      data: JSON.stringify(body),
      headers: this.headers
    })
      .then((response) => response.data)
      .catch((err) => this.handleError(err, fallbackErrorMessage));
  }
  patch(
    url: string,
    body: any,
    fallbackErrorMessage?: FallbackErrorMessageFunction
  ): Promise<any> {
    return axios({
      url: `${this.endpoint}${url}`,
      method: 'patch',
      data: JSON.stringify(body),
      headers: this.headers
    })
      .then((response) => response.data)
      .catch((err) => this.handleError(err, fallbackErrorMessage));
  }

  delete(
    url: string,
    fallbackErrorMessage?: FallbackErrorMessageFunction
  ): Promise<any> {
    return axios({
      url: `${this.endpoint}${url}`,
      method: 'delete',
      headers: this.headers
    })
      .then((response) => response.data)
      .catch((err) => this.handleError(err, fallbackErrorMessage));
  }

  upload(
    url: string,
    file: File,
    fileField: string,
    data: any,
    accessToken: string
  ) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      const xhr = new XMLHttpRequest();

      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });

      formData.append(fileField, file, file.name);

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          // accept any status in the 200 range
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.response));
          } else {
            reject(xhr.response);
          }
        }
      };
      xhr.open('POST', `${this.endpoint}${url}`, true);
      xhr.setRequestHeader(
        'Authorization',
        `Bearer ${accessToken || authService.token || ''}`
      );
      xhr.send(formData);
    });
  }
}

export default new ApiService();



// WEBPACK FOOTER //
// ./src/services/api-service.js